import { Button, Dialog, DialogActions, DialogBody, DialogContent, DialogSurface, DialogTitle, Field, Combobox, Option, Table, TableHeader, TableRow, TableHeaderCell, TableBody, TableCell, Checkbox, Dropdown, MessageBar, MessageBarBody } from "@fluentui/react-components";
import { Organization } from "../../models/organizations";
import { FC, useEffect, useState } from "react";
import { User } from "../../models/users";
import { Module } from "../../models/module";
import { getModulesByOrganization } from "../../services/moduleService";
import { RoleDTO } from "../../models/role";
import { createUserRoleAssignment, deleteUserRoleAssignment, getRolesByType } from "../../services/roleService";
import { getUserWithRolesAndModules, updateUser } from "../../services/usersService";
import apiClient from "../../services/api";
import { useAppContext } from "../../AppContext";
import ConfirmationDialog from "./ConfirmationDialog";
import { getUserGroupsByModule, getUserGroupsByModuleAndOrganizationID, updateUserGroup } from "../../services/userGroupService";
import { UpdateUserGroup } from "../../models/userGroup";
import { DismissRegular } from "@fluentui/react-icons";

type Props = {
  onClose: () => void;
  organizations: Organization[];
  user: User;
  open: boolean;
  onRoleAssigned?: () => void;
  initialAssignment?: any;
  // When provided, locks the dialog to this specific userGroup: the tendering
  // and user group selection are preselected and disabled, restricting the
  // dialog to only editing the target user's roles within this group.
  presetUserGroup?: any;
};

const AddRolesDialog: FC<Props> = ({onClose, organizations, user, open, onRoleAssigned, initialAssignment, presetUserGroup}) => {
  const {user: loggedUser} = useAppContext();

  // Ramavtalsansvarig can only edit the Behörighetsansvarig role for group members,
  // unless they're already behörighetsansvarig themselves in this specific group.
  const isRamavtalsansvarig = loggedUser?.roles?.some((r: RoleDTO) => r.roleName === 'Ramavtalsansvarig') ?? false;
  const isBehorighetsansvarigInGroup = presetUserGroup?.userGroupAssignments?.some((a: any) => a.user.userID === loggedUser?.user?.userID && a.roles?.some((r: any) => r.roleName === 'Behörighetsansvarig')) ?? false;
  // A Kontoadmin manages all groups and roles within the ramavtal they administer
  const kontoadminModuleIDs: number[] = loggedUser?.assignments
    ?.filter((a: any) => a.role.roleName === 'Kontoadmin')
    .map((a: any) => a.module.moduleID) ?? [];
  const isKontoadmin = kontoadminModuleIDs.length > 0;
  const restrictToCoordinatorOnly = isRamavtalsansvarig && !isKontoadmin && !isBehorighetsansvarigInGroup;

  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [selectedTenderingID, setSelectedTenderingID] = useState<string>("");
  const [searchValue, setSearchValue] = useState<string>("");
  const [modules, setModules] = useState<Module[]>([]);
  const [tenderingModules, setTenderingModules] = useState<Module[]>([]);
  const [selectedModuleId, setSelectedModuleId] = useState<string>("");
  const [moduleSearchValue, setModuleSearchValue] = useState<string>("");
  const [roles, setRoles] = useState<RoleDTO[]>([]);
  const [selectedRoleId, setSelectedRoleId] = useState<number | null>(null);
  const [selectedRoleIds, setSelectedRoleIds] = useState<number[]>([]);
  const [userAssignments, setUserAssignments] = useState<any[]>([]);
  const [originalRoleId, setOriginalRoleId] = useState<number | null>(null);
  const [originalRoleIds, setOriginalRoleIds] = useState<number[]>([]);
  const [isSaving, setIsSaving] = useState(false);
  const [isProcurerUser, setIsProcurerUser] = useState(false);
  const [isAdminUser, setIsAdminUser] = useState(false);
  const [isConfirmationDialogOpen, setIsConfirmationDialogOpen] = useState<boolean>(false);
  const [confirmationText, setConfirmationText] = useState<string>("");
  const [confirmationTitle, setConfirmationTitle] = useState<string>("");
  const [selectedUserGroupId, setSelectedUserGroupId] = useState<string>("");
  const [selectedUserGroupName, setSelectedUserGroupName] = useState<string>("");
  const [userGroups, setUserGroups] = useState<any[]>([]);
  const [directRoles, setDirectRoles] = useState<RoleDTO[]>([]);
  const [selectedDirectRoleId, setSelectedDirectRoleId] = useState<number | null>(null);
  const [originalDirectRoleId, setOriginalDirectRoleId] = useState<number | null>(null);

  useEffect(() => {
    if(open){
      // When opened with a preset userGroup (e.g. from UserGroupMembersDrawer), the
      // target `user` is a lightweight group member entry that may not carry
      // organizationID, so the procurer/admin classification is already implied
      // by the fact a userGroup context was passed in.
      setIsProcurerUser(presetUserGroup ? true : user.organizationID > 2);
      setIsAdminUser(presetUserGroup ? false : user.organizationID == 2);
      setSelectedTenderingID("");
      setSearchValue("");
      setTenderingModules(setUserTenderingModules());
      setSelectedModuleId("");
      setModuleSearchValue("");
      setSelectedRoleId(null);
      setSelectedRoleIds([]);
      setSelectedUserGroupId("");
      setSelectedUserGroupName("");
      setDirectRoles([]);
      setSelectedDirectRoleId(null);
      setOriginalDirectRoleId(null);
      setErrorMessage(null);

      // Fetch user assignments
      const fetchUserAssignments = async () => {
        try {
          const userData = await getUserWithRolesAndModules(user.userID);
          setUserAssignments(userData.assignments || []);
          console.log(isAdminUser);
        } catch (error) {
          console.error('Error fetching user assignments:', error);
        }
      };
      
      fetchUserAssignments();
    }
  }, [open])

  useEffect(() => {
    if(isAdminUser){
      setSelectedModuleId((1).toString());
      getRoles(1, (1).toString());
    } else if (presetUserGroup) {
      const tenderingID = presetUserGroup.moduleID.toString();
      const tenderingModule = tenderingModules.find((m: Module) => m.moduleID.toString() === tenderingID);
      setSelectedTenderingID(tenderingID);
      setSearchValue(tenderingModule ? tenderingModule.moduleName : "");
      setUserGroups([presetUserGroup]);
      setSelectedUserGroupId(presetUserGroup.userGroupID.toString());
      const currentRoleName = getUserRoleNameInGroup(presetUserGroup);
      setSelectedUserGroupName(currentRoleName ? `${presetUserGroup.userGroupName} (${currentRoleName})` : presetUserGroup.userGroupName);
      getRoles(2, tenderingID, presetUserGroup.userGroupID.toString(), presetUserGroup);
    } else if (initialAssignment && !isProcurerUser) {
      const tenderingID = initialAssignment.module.modulePath.split('_')[2];
      const tenderingModule = tenderingModules.find((m: Module) => m.moduleID.toString() === tenderingID);
      if (tenderingModule) {
        setSelectedTenderingID(tenderingID);
        setSearchValue(tenderingModule.moduleName);
        const modulesResult = loggedUser.modules.filter((m: Module) =>
          m.moduleType === "Tender" && m.modulePath.split('_')[2] === tenderingID
        );
        setModules(modulesResult);
        const moduleID = initialAssignment.module.moduleID.toString();
        setSelectedModuleId(moduleID);
        setModuleSearchValue(initialAssignment.module.moduleName);
        getRoles(3, moduleID);
      }
    }
  }, [userAssignments])

  const setUserTenderingModules = () => {
    let tenderingModules: Module[] = [];

    if(loggedUser.roles.some((r: RoleDTO) => r.roleType === "Procurer")){
      tenderingModules = loggedUser.modules.filter((m: Module) => m.moduleType === "Tendering"
      && (kontoadminModuleIDs.includes(m.moduleID)
        || loggedUser.userGroupAssignments.some((uga: any) => uga.userGroup.moduleID === m.moduleID && uga.userGroup.userGroupAssignments?.some((member: any) =>
          member.user?.userID === loggedUser.user.userID && member.roles?.some((r: any) => r.roleName === "Behörighetsansvarig")
        ))));
    }
    else{
      tenderingModules = loggedUser.modules.filter((m: Module) => m.moduleType === "Tendering");
    }

    return tenderingModules;
  }

  const filteredModules = modules.filter(mod => 
    mod.moduleName.toLowerCase().includes(moduleSearchValue.toLowerCase())
  );

  const handleTenderingSelect = async (tenderingID: string) => {
    setSelectedTenderingID(tenderingID);
    setModules([]);
    setRoles([]);
    setUserGroups([]);
    setSelectedRoleId(null);
    setSelectedRoleIds([]);
    setOriginalRoleId(null);
    setOriginalRoleIds([]);
    setSelectedUserGroupId("");
    setSelectedUserGroupName("");
    const selectedTendering = tenderingModules.find(org => org.moduleID.toString() === tenderingID);
    if (selectedTendering) {
      const userTypeID = isProcurerUser ? 2 : 3;
      if(userTypeID === 2){
        setUserGroups([]);
        if(loggedUser.roles.some((r: RoleDTO) => r.roleType === "General")){
          const ugResult = await getUserGroupsByModuleAndOrganizationID(+tenderingID, user.organizationID);
          setUserGroups(ugResult);
        }
        else if(kontoadminModuleIDs.includes(+tenderingID)){
          const ugResult = await getUserGroupsByModuleAndOrganizationID(+tenderingID, loggedUser.organization.organizationID);
          setUserGroups(ugResult);
        }
        else{
          setUserGroups(loggedUser.userGroupAssignments.filter((uga: any) => uga.userGroup.moduleID == +tenderingID && uga.userGroup.userGroupAssignments?.some((member: any) =>
            member.user?.userID === loggedUser.user.userID && member.roles?.some((r: any) => r.roleName === "Behörighetsansvarig")
          )).map((uga: any) => uga.userGroup));
        }

        // Fetch direct procurer roles (not tied to a userGroup)
        const userGroupRoleNames = ["Behörighetsansvarig", "Kommunanvändare"];
        const excludedFromDirect = [...userGroupRoleNames, "Ramavtalsansvarig"];
        const allProcurerRoles = await getRolesByType("Procurer");
        const newDirectRoles = allProcurerRoles.filter((r: RoleDTO) => !excludedFromDirect.includes(r.roleName));
        setDirectRoles(newDirectRoles);

        const existingDirectAssignment = userAssignments.find((assignment: any) =>
          assignment.module?.moduleID?.toString() === tenderingID &&
          !userGroupRoleNames.includes(assignment.role.roleName)
        );
        if (existingDirectAssignment) {
          setSelectedDirectRoleId(existingDirectAssignment.role.roleID);
          setOriginalDirectRoleId(existingDirectAssignment.role.roleID);
        } else {
          setSelectedDirectRoleId(null);
          setOriginalDirectRoleId(null);
        }
      }
      setSearchValue(selectedTendering.moduleName);
      if(userTypeID === 3){
        const modulesResult = loggedUser.modules.filter((m: Module) => 
          m.moduleType == "Tender" && m.modulePath.split('_')[2] == tenderingID
        );
        setModules(modulesResult);
      }
    };
  };

  const handleModuleSelect = (moduleID: string) => {
    setSelectedModuleId(moduleID);
    console.log(moduleID);
    const selectedMod = modules.find(mod => mod.moduleID.toString() === moduleID);
    console.log(moduleID);
    if (selectedMod) {
      setModuleSearchValue(selectedMod.moduleName);
      getRoles(3, moduleID);
    }
  };

  const handleUserGroupSelect = (userGroupID: string) => {
    setSelectedUserGroupId(userGroupID);
    console.log(selectedModuleId);
    const selectedGroup = userGroups.find((group: any) => group.userGroupID.toString() === userGroupID);
    if(selectedGroup){
      const currentRoleName = getUserRoleNameInGroup(selectedGroup);
      setSelectedUserGroupName(currentRoleName ? `${selectedGroup.userGroupName} (${currentRoleName})` : selectedGroup.userGroupName);
      getRoles(2, selectedTenderingID, userGroupID);
    }
  }

  // Determine the user's membership entry within a specific userGroup, so
  // role checkboxes reflect the group actually selected in the dropdown,
  // rather than any assignment sharing the same module.
  const groupRoleNames = ["Behörighetsansvarig", "Kommunanvändare"];

  const getGroupMembership = (group: any) => {
    return group?.userGroupAssignments?.find((uga: any) => uga.user.userID === user.userID) ?? null;
  };

  // The membership's own `roles` list (sourced from UserRoleAssignment rows)
  // is the ground truth for which role(s) the user has in this specific
  // group - the legacy `isAdmin` flag isn't reliable when a user belongs to
  // multiple groups for the same module, so it's only used as a fallback.
  const getUserRoleNamesInGroup = (group: any): string[] => {
    const membership = getGroupMembership(group);
    if (!membership) return [];
    if (membership.roles && membership.roles.length > 0) {
      return membership.roles
        .map((r: any) => r.roleName)
        .filter((name: string) => groupRoleNames.includes(name));
    }
    return [membership.isAdmin ? "Behörighetsansvarig" : "Kommunanvändare"];
  };

  const getUserRoleNameInGroup = (group: any) => {
    const roleNames = getUserRoleNamesInGroup(group);
    return roleNames.length > 0 ? roleNames.join(" & ") : null;
  };

  const getRoles = async (typeID: number, moduleIdToCheck?: string, ugIDToCheck?: string, groupOverride?: any) => {
    const roleTypeDictionary: { [key: number]: string } = {
      1: "General",
      2: "Procurer",
      3: "Supplier"
    };

    try{
      const roleResult = await getRolesByType(roleTypeDictionary[typeID]);
      setRoles(roleResult);

      const moduleIdToUse = moduleIdToCheck || selectedModuleId;

      if(ugIDToCheck){
        // Only mark roles as selected if the user is actually a member of
        // the specific userGroup selected in the dropdown - a shared
        // moduleID isn't enough, since multiple groups can exist per module.
        // groupOverride avoids a stale-state read from `userGroups` when this is
        // called synchronously right after setUserGroups() (e.g. preset flow).
        const selectedGroup = groupOverride ?? userGroups.find((g: any) => g.userGroupID.toString() === ugIDToCheck);
        const roleNamesInGroup = getUserRoleNamesInGroup(selectedGroup);
        const roleIds = roleResult.filter((r: RoleDTO) => roleNamesInGroup.includes(r.roleName)).map((r: RoleDTO) => r.roleID);
        setSelectedRoleIds(roleIds);
        setOriginalRoleIds(roleIds);
        return;
      }
      
      // Check if user already has a role in this organization/module
      if (userAssignments.length > 0) {
        let existingAssignment = null;

        {
          // For non-procurer users, find all assignments for this module
          if (typeID === 3 || typeID === 1) {
            const existingAssignments = userAssignments.filter((assignment: any) => {
              const matches = assignment.module?.moduleID?.toString() === moduleIdToUse;
              console.log(`Checking assignment module ${assignment.module?.moduleID} against selected ${moduleIdToUse}:`, matches);
              return matches;
            });
            
            console.log('Existing assignments found:', existingAssignments);
            if (existingAssignments.length > 0) {
              const roleIds = existingAssignments.map((a: any) => a.role.roleID);
              console.log('Setting selectedRoleIds to:', roleIds);
              setSelectedRoleIds(roleIds);
              setOriginalRoleIds(roleIds);
            } else {
              console.log('No assignments found, clearing selection');
              setSelectedRoleIds([]);
              setOriginalRoleIds([]);
            }
          } else {
            existingAssignment = userAssignments.find((assignment: any) => {
              const matches = assignment.module?.moduleID?.toString() === moduleIdToUse;
              console.log(`Checking assignment module ${assignment.module?.moduleID} against selected ${moduleIdToUse}:`, matches);
              return matches;
            });
            
            console.log('Existing assignment found:', existingAssignment);
            if (existingAssignment) {
              console.log('Setting selectedRoleId to:', existingAssignment.role.roleID);
              setSelectedRoleId(existingAssignment.role.roleID);
              setOriginalRoleId(existingAssignment.role.roleID);
            } else {
              console.log('No assignment found, clearing selection');
              setSelectedRoleId(null);
              setOriginalRoleId(null);
            }
          }
        }
      }
    }
    catch (error) {
      console.error('Error fetching roles:', error);
    }
  }

  const handleRoleToggle = (roleId: number, checked: boolean) => {
    if (checked) {
      setSelectedRoleIds([...selectedRoleIds, roleId]);
    } else {
      setSelectedRoleIds(selectedRoleIds.filter(id => id !== roleId));
    }
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {

      // Determine the module ID to use
      let moduleIdToUse = null;
      let updatedUserGroup: UpdateUserGroup;
      if(isProcurerUser){
        moduleIdToUse = selectedTenderingID ? parseInt(selectedTenderingID) : null;
      }
      else{
        moduleIdToUse = parseInt(selectedModuleId);
      }

      if (!moduleIdToUse) {
        console.error('No module found for assignment');
        return;
      }

      if (isProcurerUser) {
        // Handle direct role assignment (no userGroup)
        if (originalDirectRoleId && selectedDirectRoleId !== originalDirectRoleId) {
          await deleteUserRoleAssignment(user.userID, originalDirectRoleId, moduleIdToUse);
        }
        if (selectedDirectRoleId && selectedDirectRoleId !== originalDirectRoleId) {
          await createUserRoleAssignment(user.userID, selectedDirectRoleId, moduleIdToUse);
        }

        // Handle userGroup-based role assignment
        if (selectedUserGroupId) {
          const userGroup = userGroups.find((ug: any) => ug.userGroupID.toString() === selectedUserGroupId);
          const memberRole = roles.find((r: RoleDTO) => r.roleName === "Kommunanvändare");
          const adminRole = roles.find((r: RoleDTO) => r.roleName === "Behörighetsansvarig");

          const otherEmails = userGroup.userGroupAssignments
            .filter((uga: any) => uga.user.userID !== user.userID)
            .map((uga: any) => ({
              Email: uga.user.email,
              IsAdmin: uga.roles && uga.roles.length > 0
                ? uga.roles.some((r: any) => r.roleName === "Behörighetsansvarig")
                : uga.isAdmin
            }));

          const newUserEntries: { Email: string; IsAdmin: boolean }[] = [];
          if (memberRole && selectedRoleIds.includes(memberRole.roleID)) {
            newUserEntries.push({ Email: user.email, IsAdmin: false });
          }
          if (adminRole && selectedRoleIds.includes(adminRole.roleID)) {
            newUserEntries.push({ Email: user.email, IsAdmin: true });
          }

          const updatedUserGroup: UpdateUserGroup = {
            UserGroupID: userGroup.userGroupID,
            Emails: [...otherEmails, ...newUserEntries]
          };
          await updateUserGroup(updatedUserGroup);

          // Sync the explicit role links for this user group so the backend
          // can tell which user group the assignment belongs to.
          const rolesToRemove = originalRoleIds.filter(roleId => !selectedRoleIds.includes(roleId));
          for (const roleId of rolesToRemove) {
            await deleteUserRoleAssignment(user.userID, roleId, moduleIdToUse, userGroup.userGroupID);
          }

          const rolesToAdd = selectedRoleIds.filter(roleId => !originalRoleIds.includes(roleId));
          for (const roleId of rolesToAdd) {
            await createUserRoleAssignment(user.userID, roleId, moduleIdToUse, userGroup.userGroupID);
          }
        }
      } else {
        // Non-procurer users: handle multiple role assignments
        // Remove roles that were deselected
        const rolesToRemove = originalRoleIds.filter(roleId => !selectedRoleIds.includes(roleId));
        for (const roleId of rolesToRemove) {
          await deleteUserRoleAssignment(user.userID, roleId, moduleIdToUse);
        }
        
        // Add newly selected roles
        const rolesToAdd = selectedRoleIds.filter(roleId => !originalRoleIds.includes(roleId));
        for (const roleId of rolesToAdd) {
          await createUserRoleAssignment(user.userID, roleId, moduleIdToUse);
        }
      }

      // Notify parent component that role was assigned
      if (onRoleAssigned) {
        onRoleAssigned();
      }

      onClose();
    } catch (error: any) {
      console.error('Error saving role assignment:', error);
      setErrorMessage(error?.response?.status === 409
        ? (error?.response?.data?.message ?? 'Gränsen för antal kontoadmin är nådd.')
        : 'Behörigheten kunde inte sparas.');
    } finally {
      setIsSaving(false);
    }
  };

  const openConfirmationDialog = () => {
    setConfirmationTitle("Bekräfta ändring");
    setConfirmationText("Är du säker på att du vill ändra användarens roll?");
    setIsConfirmationDialogOpen(true);
  }

  const setNewTenderManager = async () => {
    setIsSaving(true);
    const tenderRole = roles.find(role => role.roleName === "Föreståndare");
    
    try{
      await createUserRoleAssignment(user.userID, tenderRole!.roleID, parseInt(selectedModuleId));
    }
    catch(error){
      console.error('Error setting new tender manager:', error);
    }
    finally{
      if (onRoleAssigned) {
        onRoleAssigned();
      }
      onClose();
      setIsSaving(false);
    }
  }
  
  const setNewTenderingResponsible = async () => {
    setIsSaving(true);
    const roleResult = await getRolesByType("Procurer");

    const tenderRole = roleResult.find((role:RoleDTO) => role.roleName === "Ramavtalsansvarig");

    try{
      await createUserRoleAssignment(user.userID, tenderRole!.roleID, parseInt(selectedTenderingID));
    }
    catch(error){
      console.error('Error setting new tendering responsible:', error);
    }
    finally{
      if (onRoleAssigned) {
        onRoleAssigned();
      }
      onClose();
      setIsSaving(false);
    }
  }

  return(
    <>
    <Dialog
      open={open}
      onOpenChange={(_event, data) => !data.open && onClose()}
      modalType="alert"
    >
      <DialogSurface>
        <DialogBody>
          <DialogTitle            
          action={
            <Button
              appearance="subtle"
              shape="circular"
              aria-label="close"
              icon={<DismissRegular />}
              onClick={onClose}
            />
          }>Inställningar</DialogTitle>
          <DialogContent>
            {errorMessage && (
              <MessageBar intent="error" className="mb-3">
                <MessageBarBody>{errorMessage}</MessageBarBody>
              </MessageBar>
            )}
            {!isAdminUser && <Field label="Ramavtal:">
              <Combobox
                placeholder="Välj ramavtal"
                value={searchValue}
                disabled={!!presetUserGroup}
                onOptionSelect={(_event, data) => {
                  if (data.optionValue) {
                    handleTenderingSelect(data.optionValue);
                  }
                }}
                onInput={(e) => setSearchValue(e.currentTarget.value)}
                positioning={{autoSize: false}}
                listbox={{
                  style: {
                    maxHeight: '300px',
                    overflowY: 'auto'
                  },
                }}
              >
                {tenderingModules.map((mod) => (
                  <Option key={mod.moduleID} value={mod.moduleID.toString()}>
                    {mod.moduleName}
                  </Option>
                ))}
              </Combobox>
            </Field>}
            { modules.length > 0 && (
              <Field label="Avdelning:">
                <Combobox
                  placeholder="Välj avdelning"
                  value={moduleSearchValue}
                  onOptionSelect={(_event, data) => {
                    if (data.optionValue) {
                      handleModuleSelect(data.optionValue);
                    }
                  }}
                  onInput={(e) => setModuleSearchValue(e.currentTarget.value)}
                  positioning={{autoSize: false}}
                  listbox={{
                    style: {
                      maxHeight: '300px',
                      overflowY: 'auto'
                    },
                  }}
                >
                  {filteredModules.map((mod) => (
                    <Option key={mod.moduleID} value={mod.moduleID.toString()}>
                      {mod.moduleName}
                    </Option>
                  ))}
                </Combobox>
              </Field>
            )}
            {(userGroups.length > 0) ? (
              <Field label="Grupp:">
                <Dropdown
                  placeholder="Välj grupp"
                  value={selectedUserGroupName}
                  disabled={!!presetUserGroup}
                  onOptionSelect={(_event, data) => {
                    if (data.optionValue) {
                      handleUserGroupSelect(data.optionValue);
                    }
                  }}
                  positioning={{autoSize: false}}
                  listbox={{
                    style: {
                      maxHeight: '300px',
                      overflowY: 'auto'
                    },
                  }}
                >
                  {userGroups.map((group: any) => {
                    const currentRoleName = getUserRoleNameInGroup(group);
                    return (
                      <Option key={group.userGroupID} value={group.userGroupID.toString()}>
                        {currentRoleName ? `${group.userGroupName} (${currentRoleName})` : group.userGroupName}
                      </Option>
                    );
                  })}
                </Dropdown>
              </Field>
            ) : ((isProcurerUser && selectedTenderingID) ? "Inga grupper hittades för det valda ramavtalet." : "")}
            {isProcurerUser && directRoles.length > 0 && selectedTenderingID && (
              <div className="mt-4">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHeaderCell>Roll</TableHeaderCell>
                      <TableHeaderCell>Medlem</TableHeaderCell>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {directRoles.map((role) => (
                      <TableRow key={role.roleID}>
                        <TableCell>
                          {organizations.find(o => o.organizationID === user.organizationID)?.organizationName} {role.roleName}
                        </TableCell>
                        <TableCell>
                          <Checkbox
                            checked={selectedDirectRoleId === role.roleID}
                            onChange={(_e, data) => data.checked ? setSelectedDirectRoleId(role.roleID) : setSelectedDirectRoleId(null)}
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
            {roles.length > 0 && (isProcurerUser ? selectedUserGroupId : selectedModuleId || isAdminUser) && (
              <div className="mt-4">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHeaderCell>{isAdminUser ? 'Roll' : 'Grupp'}</TableHeaderCell>
                      <TableHeaderCell>Medlem</TableHeaderCell>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {roles.filter((role: RoleDTO) => isProcurerUser
                      ? ["Behörighetsansvarig", "Kommunanvändare"].includes(role.roleName) && !(restrictToCoordinatorOnly && role.roleName === "Kommunanvändare")
                      : role.roleName !== "Ramavtalsansvarig" && role.roleName !== "Föreståndare").map((role) => (
                      <TableRow key={role.roleID}>
                        <TableCell>
                          {isProcurerUser ? organizations.find(o => o.organizationID === user.organizationID)?.organizationName : moduleSearchValue} {role.roleName}
                        </TableCell>
                        <TableCell>
                          <Checkbox
                            checked={selectedRoleIds.includes(role.roleID)}
                            onChange={(e, data) => handleRoleToggle(role.roleID, data.checked as boolean)}
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </DialogContent>
          {isProcurerUser && !presetUserGroup && <Button onClick={openConfirmationDialog} disabled={isProcurerUser ? (searchValue === "") : (moduleSearchValue === "")} >
            {isProcurerUser ? "Tilldela ramavtalansvarig" : "Tilldela föreståndare"}
          </Button>}
          <DialogActions>
            <Button onClick={onClose} disabled={isSaving}>Avbryt</Button>
            <Button 
              appearance="primary" 
              onClick={handleSave}
              // A group member must keep at least one role here - unchecking both would
              // drop them from the group's Emails list and delete the role link out from
              // under the explicit deleteUserRoleAssignment call further down in handleSave.
              disabled={isAdminUser ? false : isSaving || !selectedTenderingID || (isProcurerUser && !!selectedUserGroupId && selectedRoleIds.length === 0)}
            >
              {isSaving ? "Sparar..." : "Spara"}
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
    <ConfirmationDialog
      isOpen={isConfirmationDialogOpen}
      onClose={() => setIsConfirmationDialogOpen(false)}
      onConfirm={() => {isProcurerUser ? setNewTenderingResponsible() : setNewTenderManager(); setIsConfirmationDialogOpen(false);}}
      title={confirmationTitle}
      text={confirmationText}
    />
    </>
  )
}

export default AddRolesDialog;